import { formatPhoneForWhatsApp } from "../../../../bookings/view-booking2/utils/phone";

// utils/processContactsCSV.ts
export interface FileContact {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
}

export interface ErrorContact {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  unknownFieldsArray: unknown[] | undefined;
}

export interface ContactsFieldsMapped {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
}

// ---------------------------------------------------------------------------
// Dependency-free RFC4180-style CSV parser.
//
// The previous implementation did rawText.split("\n") then row.split(","),
// which breaks the moment a field is quoted and contains a comma or an
// embedded newline (e.g. a name exported from Excel as "Maggie Makanyire\n").
// That single bad field shifts every column for every row after it, which is
// what produced long runs of blank/garbage rows and stray `"` characters.
//
// This parser walks the raw text character-by-character and understands:
//   - quoted fields: "like, this"
//   - escaped quotes inside a quoted field: "she said ""hi"""
//   - commas and newlines inside quoted fields (they do NOT split the field/row)
//   - both \n and \r\n line endings
// ---------------------------------------------------------------------------
const parseCsv = (rawText: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  const text = rawText.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"'; // escaped quote
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      continue;
    }

    if (char === ",") {
      row.push(field);
      field = "";
      continue;
    }

    if (char === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      continue;
    }

    field += char;
  }

  // flush the last field/row (files don't always end with a trailing newline)
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows;
};

// A row counts as "blank" if every cell is empty once trimmed. These show up
// as spacer rows between sheets/sections in exported files and should never
// be counted as contacts.
const isBlankRow = (row: string[]): boolean =>
  row.every((cell) => (cell ?? "").trim() === "");

// Keywords used to recognise a "real" header row vs a title/banner row or a
// blank spacer row. A row only counts as the header once at least one cell
// looks like a known contact field.
const HEADER_KEYWORDS = [
  "firstname",
  "lastname",
  "surname",
  "name",
  "email",
  "mail",
  "phone",
  "mobile",
  "cell",
  "number",
  "contact",
];

const normalise = (value: string) =>
  (value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

const looksLikeHeaderCell = (cell: string) => {
  const n = normalise(cell);
  if (!n) return false;
  return HEADER_KEYWORDS.some((kw) => n.includes(kw));
};

// Some exports (e.g. handwritten-comment sheets) put a title like
// "HANDWRITTEN COMMENTS" in row 1 and a blank row 2 before the real
// "NAME, CONTACT NUMBER" header on row 3. Scan the first several rows and
// pick the first one that actually looks like a header, instead of always
// assuming row 0.
const findHeaderRowIndex = (parsedRows: string[][]): number => {
  const rowsToScan = Math.min(parsedRows.length, 10);
  for (let i = 0; i < rowsToScan; i++) {
    const row = parsedRows[i];
    const matchCount = row.filter(looksLikeHeaderCell).length;
    if (matchCount > 0) {
      return i;
    }
  }
  // Fall back to the old behaviour if nothing recognisable was found.
  return 0;
};

export const processContactsCSVFile = async (
  file: File,
  fieldsMapped: ContactsFieldsMapped,
): Promise<{
  message: string;
  errorOccured: boolean;
  contacts: FileContact[];
  errorContacts: ErrorContact[];
}> => {
  try {
    const rawData = await readFileAsText(file);
    const allParsedRows = parseCsv(rawData);
    const { headings, headerRowIndex } = deriveHeadings(allParsedRows);
    const correctContactLineHeadings = headings.length;
    const fieldsMappedKeys = Object.keys(
      fieldsMapped,
    ) as (keyof typeof fieldsMapped)[];
    const fieldsIndexMap: {
      field: string;
      mappedString: string;
      index: number;
    }[] = [];

    for (let i = 0; i < fieldsMappedKeys.length; i++) {
      const currentKey = fieldsMappedKeys[i];
      const currentValue = fieldsMapped[currentKey];
      const headingIndexInFile = headings.indexOf(currentValue);
      fieldsIndexMap.unshift({
        field: currentKey,
        mappedString: currentValue,
        index: headingIndexInFile,
      });
    }

    // Only the header row and everything after it are real data. Anything
    // before the header (title rows, blank spacer rows) is discarded rather
    // than being treated as a malformed data row. Fully blank rows anywhere
    // in the body (spacer rows between sheets/sections) are dropped too,
    // instead of being counted as contacts or flagged as errors.
    const dataRows = allParsedRows
      .slice(headerRowIndex)
      .filter((cols) => !isBlankRow(cols));

    const contactRows = dataRows.filter(
      (cols) => cols.length === correctContactLineHeadings,
    );
    const errorRows = dataRows.filter(
      (cols) => cols.length !== correctContactLineHeadings,
    );

    const errorContactsMapped: ErrorContact[] = errorRows.map(
      (errorContact, index) => {
        const extractedIndex = extractIndexs(fieldsIndexMap);
        const isSameColumn =
          extractedIndex.firstNameFileIndex ===
          extractedIndex.lastNameFileIndex;

        const firstName = isSameColumn
          ? (errorContact[extractedIndex.firstNameFileIndex]?.split(" ")[0] ??
            "")
          : errorContact[extractedIndex.firstNameFileIndex];
        const lastName = isSameColumn
          ? (errorContact[extractedIndex.firstNameFileIndex]
              ?.split(" ")
              .slice(1)
              .join(" ") ?? "")
          : errorContact[extractedIndex.lastNameFileIndex];

        if (errorContact.length > correctContactLineHeadings) {
          const unknownFieldsArray = errorContact.slice(
            correctContactLineHeadings,
          );
          return {
            id: index,
            first_name: firstName,
            last_name: lastName,
            email: errorContact[extractedIndex.emailFileIndex],
            phone_number: errorContact[extractedIndex.phoneNumberFileIndex],
            unknownFieldsArray: [...unknownFieldsArray],
          };
        }

        return {
          id: index,
          first_name: firstName,
          last_name: lastName,
          email: errorContact[extractedIndex.emailFileIndex],
          phone_number: errorContact[extractedIndex.phoneNumberFileIndex],
          unknownFieldsArray: undefined,
        };
      },
    );

    let invalidPhoneCount = 0;

    const contacts: FileContact[] = contactRows.map((cols, index) => {
      const firstNameFileIndex = fieldsIndexMap.filter(
        (obj) => obj.field === "first_name",
      )[0].index;
      const lastNameFileIndex = fieldsIndexMap.filter(
        (obj) => obj.field === "last_name",
      )[0].index;
      const emailFileIndex = fieldsIndexMap.filter(
        (obj) => obj.field === "email",
      )[0].index;
      const phoneNumberFileIndex = fieldsIndexMap.filter(
        (obj) => obj.field === "phone_number",
      )[0].index;

      const isSameColumn = firstNameFileIndex === lastNameFileIndex;

      const rawPhone = cols[phoneNumberFileIndex];
      const normalizedPhone = formatPhoneForWhatsApp(rawPhone);
      // Couldn't parse it at all — keep the raw text so it's still visible
      // for the user to fix manually, rather than silently blanking it.
      if (rawPhone && rawPhone.trim() && !normalizedPhone) {
        invalidPhoneCount++;
      }

      return {
        id: index,
        first_name: isSameColumn
          ? (cols[firstNameFileIndex]?.split(" ")[0] ?? "")
          : cols[firstNameFileIndex],
        last_name: isSameColumn
          ? (cols[firstNameFileIndex]?.split(" ").slice(1).join(" ") ?? "")
          : cols[lastNameFileIndex],
        email: cols[emailFileIndex],
        phone_number: normalizedPhone || rawPhone,
      };
    });

    contacts.shift(); // remove the heading of the file

    return {
      message:
        invalidPhoneCount > 0
          ? `File processed successfully. ${invalidPhoneCount} row(s) have a phone number that couldn't be recognized — please check them.`
          : "File processed successfully",
      errorOccured: false,
      contacts,
      errorContacts: errorContactsMapped,
    };
  } catch (err) {
    return {
      message:
        typeof err === "string"
          ? err
          : "Failed to process the file. Please make sure it is not corrupted.",
      errorOccured: true,
      contacts: [],
      errorContacts: [],
    };
  }
};

// Guess which CSV column feeds each contact field, so the mapping step arrives
// pre-filled instead of empty (an empty mapping trips the "first/last name required"
// guard even when the dropdowns visually show a heading).
export const autoMapHeadings = (headings: string[]): ContactsFieldsMapped => {
  const pick = (matchers: ((h: string) => boolean)[]): string => {
    for (const matcher of matchers) {
      const found = headings.find((h) => matcher(normalise(h)));
      if (found) return found;
    }
    return "";
  };

  const firstName = pick([
    (h) => h.includes("firstname"),
    (h) => h.includes("first"),
  ]);
  const lastName = pick([
    (h) => h.includes("lastname"),
    (h) => h.includes("surname"),
    (h) => h.includes("last"),
  ]);

  // Files like handwritten-comment sheets only have a single combined "NAME"
  // column (no separate first/last). Fall back to mapping both first_name
  // and last_name to that same column — processContactsCSVFile already
  // splits a combined name column on whitespace when the two indexes match.
  let combinedName = "";
  if (!firstName && !lastName) {
    combinedName = pick([
      (h) => h === "name",
      (h) => h.includes("fullname"),
      (h) => h.includes("name"),
    ]);
  }

  return {
    first_name: firstName || combinedName,
    last_name: lastName || combinedName,
    email: pick([(h) => h.includes("email"), (h) => h.includes("mail")]),
    phone_number: pick([
      (h) => h.includes("phone"),
      (h) => h.includes("mobile"),
      (h) => h.includes("cell"),
      (h) => h.includes("contactnumber"),
      (h) => h.includes("number"),
    ]),
  };
};

const deriveHeadings = (
  parsedRows: string[][],
): { headings: string[]; headerRowIndex: number } => {
  const headerRowIndex = findHeaderRowIndex(parsedRows);
  return { headings: parsedRows[headerRowIndex], headerRowIndex };
};

// Back-compat: keeps the old signature (just the headings array) for any
// existing callers, e.g. ContactsFileUploadSection.
export const processHeadings = async (file: File) => {
  const rawData = await readFileAsText(file);
  const { headings } = deriveHeadings(parseCsv(rawData));
  return headings;
};

const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = e.target?.result;
      if (result) {
        resolve(result.toString());
      } else {
        reject("Failed to read file");
      }
    };

    reader.onerror = () => reject("Error while reading file");

    reader.readAsText(file);
  });
};

const extractIndexs = (
  fieldsIndexMap: {
    field: string;
    mappedString: string;
    index: number;
  }[],
): {
  firstNameFileIndex: number;
  lastNameFileIndex: number;
  emailFileIndex: number;
  phoneNumberFileIndex: number;
} => {
  const firstNameFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field === "first_name",
  )[0].index;
  const lastNameFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field === "last_name",
  )[0].index;
  const emailFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field === "email",
  )[0].index;
  const phoneNumberFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field === "phone_number",
  )[0].index;

  return {
    firstNameFileIndex,
    lastNameFileIndex,
    emailFileIndex,
    phoneNumberFileIndex,
  };
};
